<script setup lang="ts">
import { computed } from 'vue'

// Ports OAlert — inline banner. `tone` + tokens replace the legacy hardcoded hex colours.
import type { AlertProps } from './types'

import { injectUiLabels } from '@/shared/i18n'
import { Icon, type IconName } from '@/shared/ui/icon'

const visible = defineModel<boolean>('visible', { default: true })

const props = withDefaults(defineProps<AlertProps>(), {
  tone: 'info',
  closable: false,
})

const emit = defineEmits<{ close: [] }>()
const uiLabels = injectUiLabels()
const resolvedCloseLabel = computed(() => props.closeLabel ?? uiLabels.close)

function close(): void {
  visible.value = false
  emit('close')
}

const toneClasses = {
  neutral: 'bg-surface-sunken border-border',
  info: 'bg-info-soft border-[color-mix(in_srgb,var(--ui-color-info)_20%,var(--ui-color-surface))]',
  success:
    'bg-success-soft border-[color-mix(in_srgb,var(--ui-color-success)_20%,var(--ui-color-surface))]',
  warning:
    'bg-warning-soft border-[color-mix(in_srgb,var(--ui-color-warning)_20%,var(--ui-color-surface))]',
  danger:
    'bg-danger-soft border-[color-mix(in_srgb,var(--ui-color-danger)_20%,var(--ui-color-surface))]',
} as const

const toneGlyphs = {
  neutral: 'info',
  info: 'info',
  success: 'circle-check',
  warning: 'alert-triangle',
  danger: 'circle-x',
} as const satisfies Record<AlertProps['tone'] & string, IconName>

const glyphTone = {
  neutral: 'text-muted',
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
} as const
</script>

<template>
  <div
    v-if="visible"
    class="ui-alert flex items-center gap-sm rounded-lg border p-md font-sans text-[0.8125rem] leading-[1.4]"
    :class="[`ui-alert--${tone}`, toneClasses[tone]]"
    role="alert"
  >
    <slot name="icon">
      <Icon
        class="ui-alert__icon flex-none"
        :class="glyphTone[props.tone]"
        :name="toneGlyphs[props.tone]"
        weight="light"
        :size="16"
        aria-hidden="true"
      />
    </slot>
    <div class="ui-alert__content min-w-0 flex-1">
      <p
        v-if="title"
        class="ui-alert__title m-0 mb-[0.15rem] text-[var(--ui-color-fg-soft)] font-semibold"
      >
        {{ title }}
      </p>
      <div :class="glyphTone[props.tone]" class="ui-alert__text font-medium"><slot /></div>
    </div>
    <button
      v-if="closable"
      type="button"
      class="ui-alert__close flex-none border-0 bg-transparent p-0 text-[var(--ui-color-fg-soft)] cursor-pointer opacity-80 hover:opacity-100"
      :aria-label="resolvedCloseLabel"
      @click="close"
    >
      <Icon name="x" weight="light" :size="14" aria-hidden="true" />
    </button>
  </div>
</template>
